import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Aliados from "./Aliados";
import Beneficios from "./Beneficios";
import { useMembership } from '../../context/MembershipContext';
import { useDataUser } from '../../context/dataUserContext';
import { FaCrown, FaCalendarAlt, FaStar, FaCheck, FaFileInvoiceDollar } from 'react-icons/fa';
import apiClient from '../../utils/axiosPetition';
import { alertaGeneral } from '../../utils/alertas';

const ID_TIPOS = [{ value: 'CC', label: 'Cédula de ciudadanía' }, { value: 'CE', label: 'Cédula de extranjería' }, { value: 'NIT', label: 'NIT' }, { value: 'PA', label: 'Pasaporte' }];

const DoctorAliadosBeneficios = () => {
    const { isPremium, membershipStatus } = useMembership();
    const { doctorId, fullName } = useDataUser();
    const [searchParams, setSearchParams] = useSearchParams();
    const [preciosMembresia, setPreciosMembresia] = useState({ mensual: null, semestral: null, anual: null });
    const [preciosCargando, setPreciosCargando] = useState(true);
    const [pagoPlan, setPagoPlan] = useState(null);
    const [modalPremiumOpen, setModalPremiumOpen] = useState(false);
    const [modalAnimated, setModalAnimated] = useState(false);
    const [buyerForm, setBuyerForm] = useState({ firstName: '', lastName: '', idType: 'CC', idNumber: '', email: '', cellNumber: '', address: '', city: '', department: '' });
    const [submittingFactura, setSubmittingFactura] = useState(false);
    const [facturaGenerada, setFacturaGenerada] = useState(false);
    const [emailFactura, setEmailFactura] = useState('');
    const [sendingEmailFactura, setSendingEmailFactura] = useState(false);

    // Animación de entrada del modal
    useEffect(() => {
        if (modalPremiumOpen) {
            const id = requestAnimationFrame(() => {
                requestAnimationFrame(() => setModalAnimated(true));
            });
            return () => cancelAnimationFrame(id);
        } else {
            setModalAnimated(false);
        }
    }, [modalPremiumOpen]);

    // Mostrar mensaje de upgrade solo una vez si el usuario no es premium
    // Cargar precios de membresía profesional (mensual, semestral, anual)
    useEffect(() => {
        apiClient.get('/api/membresia-prestador-precios')
            .then((res) => {
                const d = res?.data || {};
                const toVal = (v) => (v != null && typeof v === 'object' && 'valor' in v ? v.valor : typeof v === 'number' ? v : null);
                setPreciosMembresia({
                    mensual: toVal(d.mensual),
                    semestral: toVal(d.semestral),
                    anual: toVal(d.anual)
                });
            })
            .catch(() => setPreciosMembresia({ mensual: null, semestral: null, anual: null }))
            .finally(() => setPreciosCargando(false));
    }, []);

    // Ref de pago para facturación (viene en la URL al volver de ePayco)
    const refPago = searchParams.get('ref') || searchParams.get('ref_payco');
    const pagoExitoso = searchParams.get('pago') === 'exitoso';
    const showModalFacturacion = Boolean(pagoExitoso && refPago && !facturaGenerada);

    // Prellenar formulario de facturación con nombre del profesional
    useEffect(() => {
        if (!showModalFacturacion || !fullName) return;
        const parts = (fullName || '').trim().split(/\s+/);
        const firstName = parts[0] || '';
        const lastName = parts.slice(1).join(' ') || '';
        setBuyerForm(f => (f.firstName || f.lastName ? f : { ...f, firstName, lastName, email: localStorage.getItem('doctorEmail') || f.email }));
    }, [showModalFacturacion, fullName]);

    // Mensaje de pago exitoso cuando no hay ref (sin modal de factura)
    useEffect(() => {
        if (pagoExitoso && !refPago) {
            alertaGeneral({ titulo: 'Pago registrado', messageHtml: 'Tu pago de membresía se ha registrado correctamente.', isInfo: true });
            setSearchParams({}, { replace: true });
        }
    }, [pagoExitoso, refPago, setSearchParams]);

    // Cargar script ePayco
    useEffect(() => {
        if (!document.getElementById('epayco-checkout-js')) {
            const script = document.createElement('script');
            script.id = 'epayco-checkout-js';
            script.src = 'https://checkout.epayco.co/checkout.js';
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    const handlePagarMembresia = async (plan) => {
        if (!doctorId) {
            alertaGeneral({ titulo: 'Error', messageHtml: 'No se encontró tu perfil. Inicia sesión nuevamente.', isError: true });
            return;
        }
        const precio = preciosMembresia[plan];
        if (precio == null) {
            alertaGeneral({ titulo: 'Error', messageHtml: 'Precio no disponible para este plan.', isError: true });
            return;
        }
        setPagoPlan(plan);
        try {
            const res = await apiClient.post('/api/epayco/procesar-pago', {
                tipo: 'membresia_prestador',
                doctorId,
                plan
            });
            const data = res.data;
            if (window.ePayco) {
                const handler = window.ePayco.checkout.configure({ key: data.publicKey, test: data.test });
                handler.open({
                    name: data.description,
                    description: data.description,
                    invoice: data.referenceCode,
                    currency: 'cop',
                    amount: data.precio,
                    tax_base: '0',
                    tax: '0',
                    country: 'CO',
                    lang: 'es',
                    email_billing: data.correo,
                    response: data.responseUrl,
                    confirmation: data.confirmationUrl,
                    x_extra1: data.x_extra1,
                    x_extra2: data.x_extra2,
                    x_extra3: data.x_extra3,
                    x_extra4: data.x_extra4,
                    x_extra5: data.x_extra5,
                    x_extra6: data.x_extra6
                });
            } else {
                alertaGeneral({ titulo: 'Error', messageHtml: 'No se pudo cargar el checkout de ePayco.', isError: true });
            }
        } catch (err) {
            alertaGeneral({
                titulo: 'Error',
                messageHtml: err.response?.data?.message || 'No se pudo iniciar el pago. Intenta de nuevo.',
                isError: true
            });
        } finally {
            setPagoPlan(null);
        }
    };

    const planLabels = { mensual: 'Mensual', semestral: 'Semestral', anual: 'Anual' };
    const planSubtitles = {
        mensual: 'Pago cada mes',
        semestral: '6 meses (mejor valor)',
        anual: '12 meses (máximo ahorro)'
    };

    const handleSubmitFacturacion = async (e) => {
        e.preventDefault();
        const { firstName, lastName, idType, idNumber, email, cellNumber, address, city, department } = buyerForm;
        if (!firstName?.trim() || !lastName?.trim() || !idNumber?.trim() || !email?.trim() || !city?.trim()) {
            alertaGeneral({ titulo: 'Datos incompletos', messageHtml: 'Completa nombre, apellido, documento, correo y ciudad del comprador para la factura.', isError: true });
            return;
        }
        setSubmittingFactura(true);
        try {
            const res = await apiClient.post('/api/epayco/datos-comprador-factura', {
                referenceCode: refPago,
                ref_payco: refPago,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                idType: idType || 'CC',
                idNumber: (idNumber || '').trim().replace(/\D/g, '').slice(0, 15) || idNumber?.trim(),
                email: (email || '').trim(),
                cellNumber: (cellNumber || '').trim() || undefined,
                address: (address || '').trim() || undefined,
                city: (city || '').trim(),
                department: (department || '').trim() || undefined
            });
            if (res.data?.facturaYaGenerada) {
                setEmailFactura(buyerForm.email?.trim() || '');
                setFacturaGenerada(true);
                setSubmittingFactura(false);
                return;
            }
            setFacturaGenerada(true);
            setEmailFactura((email || '').trim());
            alertaGeneral({ titulo: 'Factura generada', messageHtml: 'La factura electrónica se ha generado correctamente en Siigo.' });
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Error al generar la factura';
            alertaGeneral({ titulo: 'Error al generar factura', messageHtml: msg, isError: true });
        } finally {
            setSubmittingFactura(false);
        }
    };

    const handleEnviarFacturaEmail = async (e) => {
        e.preventDefault();
        const emailTrim = (emailFactura || '').trim();
        if (!emailTrim) {
            alertaGeneral({ titulo: 'Correo requerido', messageHtml: 'Indique el correo al que desea recibir la factura.', isError: true });
            return;
        }
        setSendingEmailFactura(true);
        try {
            await apiClient.post('/api/epayco/enviar-factura-membresia-email', {
                referenceCode: refPago,
                ref_payco: refPago,
                email: emailTrim
            });
            alertaGeneral({ titulo: 'Factura enviada', messageHtml: `Se ha enviado la factura al correo ${emailTrim}. Revisa tu bandeja de entrada.` });
            setSearchParams({}, { replace: true });
            setFacturaGenerada(true);
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'No se pudo enviar la factura por correo.';
            alertaGeneral({ titulo: 'Error', messageHtml: msg, isError: true });
        } finally {
            setSendingEmailFactura(false);
        }
    };

    const cerrarModalFacturacion = () => {
        setSearchParams({}, { replace: true });
        setFacturaGenerada(true);
    };

    return (
        <div className="space-y-8 pb-8">
            {/* Modal: elegir plan (mensual / semestral / anual) — diseño intuitivo con animaciones */}
            {modalPremiumOpen && (
                <div
                    className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm transition-opacity duration-300 ${modalAnimated ? 'opacity-100 bg-black/50' : 'opacity-0 bg-black/30'}`}
                    onClick={() => setModalPremiumOpen(false)}
                >
                    <div
                        className={`bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-yellow-200/60 transition-all duration-300 ease-out ${modalAnimated ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Cabecera */}
                        <div className="relative bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-600 px-6 py-5 rounded-t-2xl overflow-hidden">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(255,255,255,0.2),transparent)]" />
                            <div className="relative flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                                        <FaCrown className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-white drop-shadow-sm">Elige tu plan</h3>
                                        <p className="text-white/90 text-sm mt-0.5">Selecciona cómo quieres pagar</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setModalPremiumOpen(false)}
                                    className="text-white/90 hover:text-white p-2 rounded-xl hover:bg-white/20 transition-all duration-200 active:scale-95"
                                    aria-label="Cerrar"
                                >
                                    <span className="text-2xl leading-none">&times;</span>
                                </button>
                            </div>
                        </div>

                        {/* Contenido */}
                        <div className="p-5 pb-6 bg-gradient-to-b from-amber-50/80 to-orange-50/50">
                            {preciosCargando ? (
                                <div className="flex flex-col items-center justify-center py-10 gap-4">
                                    <div className="w-12 h-12 rounded-full border-4 border-amber-300 border-t-amber-600 animate-spin" />
                                    <p className="text-gray-500 text-sm">Cargando planes...</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {['mensual', 'semestral', 'anual'].map((plan, index) => {
                                        const precio = preciosMembresia[plan];
                                        const loadingPlan = pagoPlan === plan;
                                        const isRecomendado = plan === 'semestral';
                                        return (
                                            <div
                                                key={plan}
                                                className={`relative rounded-xl border-2 p-4 bg-white shadow-sm transition-all duration-300 ease-out hover:shadow-lg hover:scale-[1.02] hover:border-amber-400/80 active:scale-[0.99] cursor-pointer ${isRecomendado ? 'border-amber-400 shadow-md ring-2 ring-amber-200/60' : 'border-yellow-200/80'} ${modalAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
                                                style={{ transitionDelay: modalAnimated ? `${index * 80}ms` : '0ms' }}
                                                onClick={() => {
                                                    if (precio != null && !loadingPlan) {
                                                        setModalPremiumOpen(false);
                                                        handlePagarMembresia(plan);
                                                    }
                                                }}
                                            >
                                                {isRecomendado && (
                                                    <span className="absolute -top-2.5 left-4 px-2.5 py-0.5 rounded-full bg-amber-500 text-white text-xs font-bold shadow-md flex items-center gap-1">
                                                        <FaStar className="text-[10px]" /> Mejor valor
                                                    </span>
                                                )}
                                                <div className="flex items-center gap-4">
                                                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${isRecomendado ? 'bg-amber-100 text-amber-700' : 'bg-amber-50 text-amber-600'}`}>
                                                        <FaCalendarAlt className="text-xl" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="font-bold text-gray-900">{planLabels[plan]}</h4>
                                                        <p className="text-sm text-gray-600">{planSubtitles[plan]}</p>
                                                        <p className="text-lg font-bold text-amber-700 mt-1">
                                                            {precio != null ? `$${Number(precio).toLocaleString('es-CO')}` : '—'}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (precio == null || loadingPlan) return;
                                                            setModalPremiumOpen(false);
                                                            handlePagarMembresia(plan);
                                                        }}
                                                        disabled={precio == null || loadingPlan}
                                                        className="flex-shrink-0 py-3 px-5 rounded-xl font-semibold text-white bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-600 hover:from-yellow-500 hover:via-orange-600 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2"
                                                    >
                                                        {loadingPlan ? (
                                                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                        ) : (
                                                            <>
                                                                <FaCheck className="text-sm" />
                                                                Pagar
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <p className="text-center text-gray-500 text-xs mt-4">Pago seguro con ePayco</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: datos del comprador para facturación Siigo (tras pago exitoso desde aliados) */}
            {showModalFacturacion && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-amber-200">
                        <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FaFileInvoiceDollar className="text-white text-xl" />
                                <div>
                                    <h3 className="text-lg font-bold text-white">{facturaGenerada ? 'Enviar factura por correo' : 'Pago exitoso'}</h3>
                                    <p className="text-white/90 text-sm">{facturaGenerada ? 'Indica el correo para recibir el PDF' : 'Datos para la factura electrónica (Siigo)'}</p>
                                </div>
                            </div>
                            <button type="button" onClick={cerrarModalFacturacion} className="text-white/90 hover:text-white p-1 rounded-lg hover:bg-white/20 transition-colors" aria-label="Cerrar">
                                <span className="text-2xl leading-none">&times;</span>
                            </button>
                        </div>

                        {!facturaGenerada ? (
                            <form onSubmit={handleSubmitFacturacion} className="p-6 space-y-3">
                                <p className="text-gray-600 text-sm mb-4">Indica los datos del comprador para generar la factura (pueden ser los tuyos o de tu empresa).</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                                        <input type="text" value={buyerForm.firstName} onChange={e => setBuyerForm(f => ({ ...f, firstName: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Nombre" required />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
                                        <input type="text" value={buyerForm.lastName} onChange={e => setBuyerForm(f => ({ ...f, lastName: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Apellido" required />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de documento</label>
                                    <select value={buyerForm.idType} onChange={e => setBuyerForm(f => ({ ...f, idType: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                        {ID_TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Número de documento *</label>
                                    <input type="text" value={buyerForm.idNumber} onChange={e => setBuyerForm(f => ({ ...f, idNumber: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Documento" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Correo del comprador *</label>
                                    <input type="email" value={buyerForm.email} onChange={e => setBuyerForm(f => ({ ...f, email: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="correo@ejemplo.com" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Celular</label>
                                    <input type="text" value={buyerForm.cellNumber} onChange={e => setBuyerForm(f => ({ ...f, cellNumber: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Celular" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                                    <input type="text" value={buyerForm.address} onChange={e => setBuyerForm(f => ({ ...f, address: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Calle, número, barrio (opcional)" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad *</label>
                                        <input type="text" value={buyerForm.city} onChange={e => setBuyerForm(f => ({ ...f, city: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Ej. Bogotá, Medellín" required />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Departamento</label>
                                        <input type="text" value={buyerForm.department} onChange={e => setBuyerForm(f => ({ ...f, department: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Ej. Cundinamarca" />
                                    </div>
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button type="button" onClick={cerrarModalFacturacion} className="flex-1 py-2.5 px-4 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">
                                        Omitir
                                    </button>
                                    <button type="submit" disabled={submittingFactura} className="flex-1 py-2.5 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-600 hover:from-yellow-500 hover:via-orange-600 hover:to-yellow-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all">
                                        {submittingFactura ? 'Generando factura...' : 'Generar factura'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleEnviarFacturaEmail} className="p-6 space-y-4">
                                <p className="text-gray-600 text-sm">Indica el correo al que deseas que te enviemos la factura electrónica (PDF) de Siigo.</p>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico *</label>
                                    <input
                                        type="email"
                                        value={emailFactura}
                                        onChange={e => setEmailFactura(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                                        placeholder="correo@ejemplo.com"
                                        required
                                    />
                                </div>
                                <div className="flex gap-3">
                                    <button type="button" onClick={cerrarModalFacturacion} className="flex-1 py-2.5 px-4 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">
                                        No enviar
                                    </button>
                                    <button type="submit" disabled={sendingEmailFactura} className="flex-1 py-2.5 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-600 hover:from-yellow-500 hover:via-orange-600 hover:to-yellow-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all">
                                        {sendingEmailFactura ? 'Enviando...' : 'Enviar factura por correo'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {isPremium() && membershipStatus?.expiresAt && (
                <div className="mx-4 mb-2">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <p className="text-sm font-semibold text-emerald-800">
                                Membresía Premium activa
                                {membershipStatus.membershipPlan ? ` (${membershipStatus.membershipPlan})` : ''}
                            </p>
                            <p className="text-sm text-emerald-700 mt-1">
                                Vence el {new Date(membershipStatus.expiresAt).toLocaleDateString('es-CO', { dateStyle: 'long' })}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setModalPremiumOpen(true)}
                            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
                        >
                            Renovar membresía
                        </button>
                    </div>
                </div>
            )}

            {/* Componentes de Beneficios y Aliados */}
            <Beneficios />
            <Aliados />
        </div>
    )
}

export default DoctorAliadosBeneficios;